import { SuccessModal } from '@components/atomic/organisms';
import { IcoDownloadCloudArrow, IcoDownloadCloudArrowSmall } from '@icons';
import { downloadProjectEntityChecklistsDocument, downloadProjectEntityDocument } from '@lib/api';
import { exportPopulatedProjectTemplate, ProjectEntityType } from '@lib/api_ts/templates';
import { useMutation } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Button, InputGroup, Modal, Spinner } from 'react-bootstrap';
import { useLocation, useParams } from 'react-router-dom';

interface DownloadEntitiesModalProps {
	entity: {
		translate: string;
		entityType: string;
	};
}

const DownloadEntitiesModal: React.FC<DownloadEntitiesModalProps> = ({ entity }) => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const { projectId } = useParams<{ projectId: string }>();
	const [selectedOption, setSelectedOption] = useState<string>();

	const location = useLocation();
	const activeKey = location.hash?.replace('#', '') || 'contracts';

	const onSuccessReset = () => {
		setIsModalOpen(false);
		setSelectedOption(undefined);
	};
	const downloadEntityMutation = useMutation({
		mutationFn: () => downloadProjectEntityDocument([projectId, entity.entityType]),
		onSuccess: onSuccessReset,
	});

	const downloadPopulatedTemplateMutation = useMutation({
		mutationFn: () => exportPopulatedProjectTemplate(projectId, entity.entityType as ProjectEntityType),
		onSuccess: onSuccessReset,
	});

	const downloadChecklistEntityMutation = useMutation({
		mutationFn: () => {
			const cleanedEntityType = entity.entityType !== 'payments' ? entity.entityType : 'invoicepayments';
			return downloadProjectEntityChecklistsDocument([projectId, cleanedEntityType]);
		},
	});

	const downloadRegisFileMutation = useMutation({
		mutationFn: () => downloadProjectEntityDocument([projectId, 'regis']),
		onSuccess: onSuccessReset,
	});

	const downloadClockingsMutation = useMutation({
		mutationFn: () => downloadProjectEntityDocument([projectId, 'regis/stamps']),
		onSuccess: onSuccessReset,
	});

	const getEntityDescriptionPartByEntityKey = (key: string) => {
		switch (key) {
			case 'contracts':
				return 'di tutti gli incarichi, convenzioni e atti di attingimento caricati in piattaforma';
			case 'invoices':
				return 'di tutte le fatture caricate in piattaforma';
			case 'payments':
				return 'di tutti i pagamenti caricati in piattaforma';
			case 'timesheets':
				return 'del personale inserito in piattaforma, incluse anagrafiche, periodizzazioni e stati di validazione';
			default:
				return 'UNKNOWN KEY';
		}
	};

	const getTemplateDescriptionPartByEntityKey = (key: string) => {
		switch (key) {
			case 'contracts':
				return 'agli incarichi, convenzioni e atti di attingimento';
			case 'invoices':
				return 'alle fatture';
			case 'payments':
				return 'ai pagamenti';
			case 'timesheets':
				return 'al personale';
			default:
				return 'UNKNOWN KEY';
		}
	};

	const getChecklistEntityDescriptionPartByEntityKey = (key: string) => {
		switch (key) {
			case 'contracts':
				return 'agli incarichi e attingimenti caricati in piattaforma';
			case 'invoices':
				return 'alle fatture caricate in piattaforma';
			case 'payments':
				return 'ai pagamenti caricati in piattaforma';
			case 'timesheets':
				return 'alle schede del personale';
			default:
				return 'UNKNOWN KEY';
		}
	};

	const itemsToDownload = [
		{
			key: `${entity.entityType}-export`,
			label: `Elenco ${entity.translate}`,
			description: `Scarica il report operativo con il dettaglio ${getEntityDescriptionPartByEntityKey(entity.entityType)}. Il file consente di verificare e monitorare l'avanzamento delle attività di assurance.`,
			mutation: downloadEntityMutation,
		},
		{
			key: `checklist-${entity.entityType}-export`,
			label: `Elenco Checklist (${entity.translate})`,
			description: `Scarica il file excel per la gestione massiva dei controlli e degli stati relativi ${getChecklistEntityDescriptionPartByEntityKey(entity.entityType)}. Può essere usato come backup dei controlli già eseguiti o per modificare massivamente il risultato dell'assurance.`,
			mutation: downloadChecklistEntityMutation,
		},
		{
			key: `template-${entity.entityType}-export`,
			label: `Dati ${entity.translate} (Template compilato)`,
			description: `Scarica il file Excel con tutti i dati relativi ${getTemplateDescriptionPartByEntityKey(entity.entityType)}, già compilato e pronto per eventuali futuri upload. Può essere inoltre usato come backup degli import effettuati.`,
			mutation: downloadPopulatedTemplateMutation,
		},

		// ...(activeKey === 'invoices'
		// 	? [
		// 			{
		// 				key: `regis-export`,
		// 				label: `Elenco file REGIS`,
		// 				description:
		// 					'Lorem ipsum dolor sit amet consectetur. Sed vel vitae a leo euismod diam. Interdum ut tristique nunc morbi ullamcorper. Est ac enim vivamus id at pretium quam tincidunt nec. Vel cras ultrices viverra turpis.',
		// 				mutation: downloadRegisFileMutation,
		// 			},
		// 		]
		// 	: []),
	];

	if (activeKey === 'invoices') {
		itemsToDownload.push({
			key: 'regis-export',
			label: 'Elenco file REGIS',
			description:
				'Scarica il template excel per l’import massivo dei dati nel portale ReGiS. Il file contiene la struttura standard richiesta per la trasmissione automatica delle informazioni rendicontate.',
			mutation: downloadRegisFileMutation,
		});
		itemsToDownload.push({
			key: 'clockings-export',
			label: 'Elenco timbrature',
			description:
				"Scarica il report operativo con il dettaglio delle fatture e/o quote oggetto di timbratura di annullamento e invio a rendicontazione. Il file consente di verificare e monitorare l'avanzamento delle attività di assurance.",
			mutation: downloadClockingsMutation,
		});
	}
	return (
		<>
			<SuccessModal
				message="Il documento è in elaborazione e al completamente ti verrà inviata una notifica"
				show={downloadPopulatedTemplateMutation.isSuccess}
				onHide={() => {
					downloadPopulatedTemplateMutation.reset();
					onSuccessReset();
				}}
			/>
			<Button
				onClick={() => {
					setIsModalOpen(true);
				}}
				className="btn custom-primary-button"
				style={{
					fontWeight: 500,
					fontSize: '18px',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					gap: '0.5rem',
				}}
			>
				<IcoDownloadCloudArrowSmall fill="#1C2743" />

				<span style={{ translate: '0 1px' }}>Scarica</span>
			</Button>
			<Modal
				show={isModalOpen}
				onHide={() => {
					setIsModalOpen(false);
				}}
				centered
				size="xl"
				className="custom-modal-xl"
			>
				<Modal.Header
					style={{
						flexDirection: 'column',
						justifyContent: 'center',
						alignItems: 'center',
						gap: '1rem',
						padding: '1.5rem',
					}}
				>
					<IcoDownloadCloudArrow
						style={{
							width: '48px',
							height: '48px',
						}}
					/>
					<Modal.Title>
						<h5
							style={{
								fontWeight: 700,
								fontSize: '24px',
								textAlign: 'center',
								marginBottom: 0,
							}}
						>
							Scarica {entity.translate}
						</h5>
					</Modal.Title>

					<div>
						<p
							style={{
								marginBottom: 0,
								textAlign: 'center',
							}}
						>
							Qui puoi scaricare l'elenco in formato EXCEL.
						</p>
						<p
							style={{
								fontWeight: 700,
								marginBottom: 0,
								textAlign: 'center',
							}}
						>
							Di quale versione hai bisogno?
						</p>
					</div>
				</Modal.Header>
				<Modal.Body
					style={{
						padding: '1.5rem',
					}}
				>
					<div className="custom-download_list">
						{itemsToDownload.map((item) => (
							<InputGroup
								key={item.key}
								style={{
									display: 'grid',
									gridTemplateColumns: 'auto 1fr',
								}}
							>
								<InputGroup.Radio
									checked={selectedOption === item.key}
									onChange={() => {
										setSelectedOption(item.key);
									}}
									aria-label={item.label}
									name="download-options"
									value={item.key}
									style={{
										cursor: 'pointer',
									}}

									// className="col-1"
								/>
								<InputGroup.Text
									// className="col-11"
									style={{
										flexDirection: 'column',
										alignItems: 'flex-start',
										whiteSpace: 'normal',
										textAlign: 'left',
									}}
								>
									<span
										style={{
											fontWeight: 700,
											display: 'block',
										}}
									>
										{item.label}
									</span>
									<span
										style={{
											display: 'block',
										}}
									>
										{item.description}
									</span>
								</InputGroup.Text>
							</InputGroup>
						))}
					</div>
				</Modal.Body>
				<Modal.Footer
					style={{
						display: 'grid',
						gridTemplateColumns: '1fr 1fr',
					}}
				>
					<Button
						className="btn custom-outline-button"
						style={{
							height: '48px',
						}}
						onClick={() => {
							setIsModalOpen(false);
							setSelectedOption(undefined);
						}}
					>
						Annulla
					</Button>
					<Button
						disabled={!selectedOption || itemsToDownload.find((item) => item.key === selectedOption)?.mutation.isPending}
						className="btn custom-primary-button"
						style={{
							height: '48px',
							display: 'flex',
							alignItems: 'center',
							gap: '0.5rem',
							justifyContent: 'center',
						}}
						onClick={() => {
							const item = itemsToDownload.find((item) => item.key === selectedOption);
							item?.mutation.mutate();
						}}
					>
						{!itemsToDownload.find((item) => item.key === selectedOption)?.mutation.isPending ? (
							'Scarica'
						) : (
							<>
								<Spinner animation="border" size="sm" role="status" /> Scaricando...
							</>
						)}
					</Button>
				</Modal.Footer>
			</Modal>
		</>
	);
};

export default DownloadEntitiesModal;
